import { StyleProvider } from "@ant-design/cssinjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigProvider } from "antd";
import ErrorBoundary from "common/components/ErrorBoundary";
import AuthContextProvider from "common/contexts/AuthContext";
import CartContextProvider from "common/contexts/CartContext";
import UserContextProvider from "common/contexts/UserContext";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Login from "./Login";
import ProductionCost from "./ProductionCost";
import ProductDetails from "./ProductMapping/ProductDetails";
import ResetPassword from "./ResetPassword";
import SCMPurchaseOrderManagement from "./SCMPurchaseOrder/index";
import Signup from "./Signup";
import StoreInventory from "./StoreInventory";

const AppLayout = lazy(() => import("common/components/AppLayout"));
const Dashboard = lazy(() => import("./Dashboard"));
const FinishedGoodsDetails = lazy(() => import("./GoodsProduced"));
const GoodsAnalytics = lazy(() => import("./GoodsAnalytics"));
const RetailPlaceOrder = lazy(() => import("./RetailPlaceOrder"));
const PlaceOrder = lazy(() => import("./PlaceOrder"));
const Cart = lazy(() => import("./NewCart"));
const AllOrders = lazy(() => import("./AllOrders"));
const Suppliers = lazy(() => import("./Suppliers"));
const UserGroups = lazy(() => import("./UserGroups"));
const Customers = lazy(() => import("./Customer"));
const Representatives = lazy(() => import("./Representatives"));
const Attendance = lazy(() => import("./Attendance"));
const Payments = lazy(() => import("./Payments"));
const Profile = lazy(() => import("./Profile"));
const InvoiceManagement = lazy(() => import("./InvoiceManagement"));
const Inventory = lazy(() => import("./Inventory"));
const GRN = lazy(() => import("./GRN"));
const Yard = lazy(() => import("./Yard"));
const Report = lazy(() => import("./Reports"));
const Wiresaw = lazy(() => import("./WireSaw/Wiresaw"));
const Sale = lazy(() => import("./SaleDetails"));
const LedgerOrder = lazy(() => import("./LedgerOrder"));
const Support = lazy(() => import("./Support"));
const Coupons = lazy(() => import("./Coupons"));
const Offers = lazy(() => import("./Offers"));
const Notifications = lazy(() => import("./Notifications"));
const Subscriptions = lazy(() => import("./Subscriptions"));
const UpdateInvoice = lazy(() => import("./UpdateInvoice"));
const PaymentLanding = lazy(() => import("./PaymentLanding"));
const Broadcasts = lazy(() => import("./Broadcasts"));
const Settings = lazy(() => import("./Setting"));
const LandingPage = lazy(() => import("./LandingPage"));
const ValueMedi = lazy(() => import("./LandingPage/ValueMedi"));
const Retailer = lazy(() => import("./LandingPage/Roles/Retailer"));
const Distributor = lazy(() => import("./LandingPage/Roles/Distributor"));
const BlogList = lazy(() => import("./LandingPage/BlogList"));
const BlogPost = lazy(() => import("./LandingPage/BlogPost"));
const ContactUs = lazy(() => import("./LandingPage/ContactUs"));
const Ledgers = lazy(() => import("./Ledgers"));
const RetailCart = lazy(() => import("./RetailCart"));
const CreditConsent = lazy(() => import("./CreditConsent"));
const Tracking = lazy(() => import("./Tracking"));
const Logistics = lazy(() => import("./Logistics"));
const DrugInfo = lazy(() => import("./DrugInfo"));
const TermsAndConditions = lazy(() => import("./TermsAndConditions"));
const ErpDashboard = lazy(() => import("./ErpDashboard"));
const ErpUsers = lazy(() => import("./AllErpUsers"));
const SlabDetails = lazy(() => import("./SubBlocksEntry"));
const History = lazy(() => import("./History"));
const Cutter = lazy(() => import("./Cutter"));
const Din = lazy(() => import("./Din"));
// RMPM Components
const AllPurchaseOrders = lazy(() => import("./AllPurchaseOrders"));
const User = lazy(() => import("./CreateUser"));
const ProductInventory = lazy(() => import("./ProductView/ProductInventory"));
const SCM = lazy(() => import("./Scm"));
const Goods = lazy(() => import("./Goods"));
const PMOrder = lazy(() => import("./PmOrder"));
const ProductMapping = lazy(() => import("./ProductMapping"));
const RMPMSale = lazy(() => import("./RMPMSale"));
const RaiseQuotations = lazy(() => import("./RaiseQuotation"));
const DinData = lazy(() => import("./DinData"));
const GRNQualityCheck = lazy(() => import("./GRNQualityCheck"));
const Reservation = lazy(() => import("./Reservation"));
const BorrowRequests = lazy(() => import("./BorrowRequests"));
const Ledger = lazy(() => import("./Ledger"));
const SalesReports = lazy(() => import("./SalesReports"));
const PharmaSettings = lazy(() => import("./PharmaSettings"));
const MasterAdminReports = lazy(() => import("./MasterAdminReports"));
const FinanceAnalytics = lazy(() => import("./FinanceAnalytics"));
const SuperAdminSaleAnalytics = lazy(() => import("./SuperAdminSaleAnalytics"));
const SuperAdminInventoryAnalytics = lazy(
  () => import("./SuperAdminInventoryAnalytics")
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});
const theme = {
  token: {
    colorPrimary: "#00BFB8",
    fontFamily: "poppins",
    borderRadius: 0
  }
};

function App() {
  return (
    <StyleProvider layer>
      <ConfigProvider theme={theme}>
        <AuthContextProvider>
          <QueryClientProvider client={queryClient}>
            <CartContextProvider>
              <UserContextProvider>
                <BrowserRouter>
                  <ErrorBoundary>
                    <Suspense fallback={<div>Loading your content...</div>}>
                      <Routes>
                        <Route path="/" element={<AppLayout />}>
                          <Route index element={<History />} />
                          {/* RMPM Routes start */}
                          <Route
                            path="purchase-order/*"
                            element={<AllPurchaseOrders />}
                          />
                          <Route path="sales/*" element={<RMPMSale />} />
                          <Route path="create-user" element={<User />} />
                          <Route
                            path="product-inventory"
                            element={<ProductInventory />}
                          />
                          <Route path="scm/*" element={<SCM />} />
                          <Route
                            path="scm-po/*"
                            element={<SCMPurchaseOrderManagement />}
                          />
                          <Route path="dinData/*" element={<DinData />} />
                          <Route path="ledger/*" element={<Ledger />} />
                          <Route
                            path="pharma-settings/*"
                            element={<PharmaSettings />}
                          />
                          <Route path="goods" element={<Goods />} />
                          <Route path="pm-order/*" element={<PMOrder />} />
                          <Route
                            path="reservation/*"
                            element={<Reservation />}
                          />
                          <Route
                            path="borrow-requests/*"
                            element={<BorrowRequests />}
                          />
                          <Route
                            path="masterReports/*"
                            element={<MasterAdminReports />}
                          />
                          <Route
                            path="finance-analytics/*"
                            element={<FinanceAnalytics />}
                          />

                          <Route
                            path="/inventory-analytics/*"
                            element={<SuperAdminInventoryAnalytics />}
                          />

                          <Route
                            path="/sales-analytics/*"
                            element={<SuperAdminSaleAnalytics />}
                          />
                          {/* RMPM Routes end */}
                          <Route path="dashboard" element={<Dashboard />} />
                          <Route
                            path="GoodsProduced/*"
                            element={<FinishedGoodsDetails />}
                          />
                          <Route
                            path="goodsAnalytics/*"
                            element={<GoodsAnalytics />}
                          />
                          <Route path="suppliers/*" element={<Suppliers />} />
                          <Route path="groups/*" element={<UserGroups />} />
                          <Route path="customers/*" element={<Customers />} />
                          <Route path="ledgers" element={<Ledgers />} />
                          <Route
                            path="representatives/*"
                            element={<Representatives />}
                          />
                          <Route
                            path="storeInventory"
                            element={<StoreInventory />}
                          />
                          <Route
                            path="productionCost"
                            element={<ProductionCost />}
                          />
                          <Route
                            path="product-mapping"
                            element={<ProductMapping />}
                          />
                          <Route
                            path="product-mapping/:id"
                            element={<ProductDetails />}
                          />
                          <Route path="attendance" element={<Attendance />} />

                          <Route
                            path="retailPlaceOrder/*"
                            element={<RetailPlaceOrder />}
                          />
                          <Route
                            path="sale-quotation/*"
                            element={<RaiseQuotations />}
                          />
                          <Route path="placeOrder/*" element={<PlaceOrder />} />
                          <Route path="cart" element={<Cart />} />
                          <Route path="retailCart" element={<RetailCart />} />
                          <Route path="orders/*" element={<AllOrders />} />
                          <Route path="history/*" element={<History />} />
                          <Route path="din/*" element={<Din />} />
                          <Route
                            path="grn-qc/*"
                            element={<GRNQualityCheck />}
                          />
                          <Route
                            path="invoices/*"
                            element={<InvoiceManagement />}
                          />
                          <Route path="payments/*" element={<Payments />} />
                          <Route path="credit" element={<CreditConsent />} />
                          <Route path="profile" element={<Profile />} />
                          <Route path="grn/*" element={<GRN />} />
                          <Route path="inventory/*" element={<Inventory />} />
                          <Route path="yard" element={<Yard />} />
                          <Route path="reports/*" element={<Report />} />
                          <Route path="wiresaw" element={<Wiresaw />} />
                          <Route path="cutter" element={<Cutter />} />
                          <Route path="sale/*" element={<Sale />} />
                          <Route path="drugInfo" element={<DrugInfo />} />
                          <Route
                            path="/slab-details/*"
                            element={<SlabDetails />}
                          />
                          <Route
                            path="ledgerOrder/*"
                            element={<LedgerOrder />}
                          />
                          <Route path="support" element={<Support />} />
                          <Route path="Coupons" element={<Coupons />} />
                          <Route path="offers" element={<Offers />} />
                          <Route
                            path="notifications"
                            element={<Notifications />}
                          />
                          <Route
                            path="subscriptions"
                            element={<Subscriptions />}
                          />
                          <Route path="settings" element={<Settings />} />
                          <Route path="broadcasts" element={<Broadcasts />} />
                          <Route path="tracking" element={<Tracking />} />
                          <Route path="logistics" element={<Logistics />} />
                          <Route
                            path="erp-dashboard"
                            element={<ErpDashboard />}
                          />
                          <Route path="all-users" element={<ErpUsers />} />
                          <Route
                            path="sales-reports/*"
                            element={<SalesReports />}
                          />

                          <Route path="*" element={<History />} />
                        </Route>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/reset" element={<ResetPassword />} />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/home" element={<LandingPage />}>
                          <Route index element={<ValueMedi />} />
                          <Route path="retailer" element={<Retailer />} />
                          <Route path="distributor" element={<Distributor />} />
                          <Route path="blogs" element={<BlogList />} />
                          <Route path="blogpost/:slug" element={<BlogPost />} />
                          <Route path="contact-us" element={<ContactUs />} />
                        </Route>
                        <Route
                          path="/payment-landing"
                          element={<PaymentLanding />}
                        />
                        <Route
                          path="/updateInvoice/:customerId/:updateId"
                          element={<UpdateInvoice />}
                        />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </BrowserRouter>
                <ReactQueryDevtools initialIsOpen={false} position="bottom" />
              </UserContextProvider>
            </CartContextProvider>
          </QueryClientProvider>
        </AuthContextProvider>
      </ConfigProvider>
    </StyleProvider>
  );
}

export default App;
